import React, { RefObject, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useWatch, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconFileTypeXls, IconFilter, IconRefresh, IconX } from '@tabler/icons-react';
import SearchIcon from '@mui/icons-material/Search';

import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { IconOngoingCalls } from '@shared/assets/images/svgs';
import { CallsFilter } from '@shared/components/callsFilter/callsFilter';
import { CustomTooltip } from '@shared/components/customTooltip';
import { CampaignDto } from '@shared/services/apiService/apiService';

interface OngoingCallsToolbarProps {
  headerRef: RefObject<HTMLDivElement | null>;
  campaignData: CampaignDto;
  callsCount: number;
  isExportReportAvailable: boolean;
  onOpenReportDialog: () => void;
  formMethods: UseFormReturn<CallsFilterFormValue>;
  classes: { header_title: string };
  isExtendedSearch: boolean;
  onExtendedSearchChange: (value: boolean) => void;
}

const isActiveFilterValue = (value: string | null | undefined): boolean =>
  Boolean(value && value !== 'all');

const NUMBER_SEARCH_DEBOUNCE_MS = 200;

export const OngoingCallsToolbar: React.FC<OngoingCallsToolbarProps> = ({
  headerRef,
  campaignData,
  callsCount,
  isExportReportAvailable,
  onOpenReportDialog,
  formMethods,
  classes,
  isExtendedSearch,
  onExtendedSearchChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const filterValues = useWatch({ control: formMethods.control });
  const numberSearch = filterValues.numberSearch ?? '';
  const [searchText, setSearchText] = useState(numberSearch);

  useEffect(() => {
    setSearchText(numberSearch);
  }, [numberSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText !== numberSearch) {
        formMethods.setValue('numberSearch', searchText);
      }
    }, NUMBER_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [formMethods, numberSearch, searchText]);

  const activeSecondaryFiltersCount = [
    isActiveFilterValue(filterValues.types),
    isActiveFilterValue(filterValues.statuses),
    isActiveFilterValue(filterValues.endedReason),
    isActiveFilterValue(filterValues.start),
    isActiveFilterValue(filterValues.end),
    isActiveFilterValue(filterValues.callIdSearch),
  ].filter(Boolean).length;
  const hasActiveFilters = isActiveFilterValue(numberSearch) || activeSecondaryFiltersCount > 0;

  const handleReset = () => {
    formMethods.reset();
    setSearchText('');
  };

  return (
    <Box ref={headerRef}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          'svg path': {
            stroke: theme.palette.getContrastText(theme.palette.background.default),
          },
        }}>
        <IconOngoingCalls />
        <Typography fontSize="18px" fontWeight={600}>
          {t('campaignPage.calls')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}>
        <Box
          className={classes.header_title}
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}>
          <Typography fontSize="18px" sx={{ minWidth: '110px' }}>
            {t('campaignPage.editCampaign.calls')}
          </Typography>
          <Typography
            fontSize="18px"
            color={theme.palette.text.secondary}
            sx={{ minWidth: '42px' }}>
            ({callsCount})
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            flex: '1 1 320px',
            minWidth: 0,
          }}>
          <TextField
            size="small"
            fullWidth
            id="number-search-input"
            label={t('campaignPage.ongoingCallsTable.filter.search')}
            placeholder={t('campaignPage.ongoingCallsTable.filter.searchPlaceholder')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            sx={{ maxWidth: 300, minWidth: { xs: 0, sm: 200 }, flex: '1 1 200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('campaignPage.ongoingCallsTable.filter.clearSearch')}
                    onClick={() => setSearchText('')}>
                    <IconX size={16} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Badge
            badgeContent={activeSecondaryFiltersCount}
            color="primary"
            invisible={!activeSecondaryFiltersCount}>
            <Button
              id="ongoing-calls-filters-toggle"
              onClick={() => setIsFiltersOpen((previous) => !previous)}
              startIcon={<IconFilter size={18} />}
              aria-controls="ongoing-calls-filters"
              aria-expanded={isFiltersOpen}>
              {t('campaignPage.ongoingCallsTable.filter.filters')}
            </Button>
          </Badge>
          {hasActiveFilters && (
            <Button onClick={handleReset} startIcon={<IconRefresh size={18} />}>
              {t('campaignPage.ongoingCallsTable.filter.reset')}
            </Button>
          )}
          <CustomTooltip
            title={
              isExportReportAvailable
                ? null
                : t('campaignPage.ongoingCallsTable.report.unavailable')
            }
            placement="top"
            arrow>
            <span>
              <Button onClick={onOpenReportDialog} disabled={!isExportReportAvailable}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    whiteSpace: 'nowrap',
                    gap: 1,
                  }}>
                  {t('campaignPage.ongoingCallsTable.report.title')}
                  <IconFileTypeXls />
                </Box>
              </Button>
            </span>
          </CustomTooltip>
        </Box>
      </Box>
      <Collapse in={isFiltersOpen} timeout={0}>
        <Box
          id="ongoing-calls-filters"
          role="region"
          aria-labelledby="ongoing-calls-filters-toggle"
          sx={{ pt: 1, pb: 2 }}>
          <CallsFilter
            campaignData={campaignData}
            formMethods={formMethods}
            isExtendedSearch={isExtendedSearch}
            onExtendedSearchChange={onExtendedSearchChange}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
